import { useMsal } from "@azure/msal-react";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { graphRequest } from "../authConfig";
import { Client } from "@microsoft/microsoft-graph-client";
import { User } from "microsoft-graph";
import { AccountInfo } from "@azure/msal-browser";

export const getUserInitials = (user?: User): string => {
  const mail = user?.mail ?? user?.userPrincipalName;
  return mail?.split("@")[0] ?? "";
};
export interface IGraphService {
  user: User | undefined;
  photo: Blob | undefined;
}

const defaultGraphService: IGraphService = {
  user: undefined,
  photo: undefined,
};

// Create a context for listed algos
const context = createContext<IGraphService>(defaultGraphService);

// Use this hook to access the context
export function useGraphContext(): IGraphService {
  return useContext(context);
}

export const GraphServiceContext =
  createContext<IGraphService>(defaultGraphService);

const GraphService: FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [photo, setPhoto] = useState<Blob | undefined>(undefined);

  const { instance, accounts } = useMsal();

  useEffect(() => {
    if (user === undefined && photo === undefined) {
      const graphClient = Client.initWithMiddleware({
        authProvider: {
          ...instance,
          getAccessToken: async () => {
            const req = await instance.acquireTokenSilent({
              scopes: graphRequest.scopes,
              account: instance.getActiveAccount() || accounts[0],
            });

            return req.accessToken;
          },
        },
      });

      graphClient
        .api("/me")
        .get()
        .then((u) => setUser(u));
      graphClient
        .api("/me/photos/48x48/$value")
        .get()
        .then((p) => {
          setPhoto(p);
        })
        .catch(() => {
          setPhoto(undefined);
        });
    }
  }, [accounts, instance, photo, user]);

  return (
    <GraphServiceContext.Provider value={{ user, photo }}>
      {children}
    </GraphServiceContext.Provider>
  );
};

export const useGraphService = () => {
  const context = useContext<IGraphService>(GraphServiceContext);
  if (context === undefined) {
    throw new Error(
      "GraphServiceContext not provided. Make sure your component is a child of the AlgoService"
    );
  }
  return context;
};

export const getUsernameFromMsal = (accounts: AccountInfo[]): string => {
  if (accounts.length > 0) {
    const email = accounts[0].username || accounts[0].name;
    return email?.split("@")[0] || "default";
  }
  return "default";
};

export { GraphService };
